'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import Raven from 'raven-js';
import PropTypes from 'prop-types';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UserProfile from 'MeetingsBase/models/UserProfile';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import H4 from 'UIComponents/elements/headings/H4';
import { NOT_FOUND, USER_NOT_FOUND, MEETING_PREVIOUSLY_CANCELLED, REBOOK_CONFLICT, EXCHANGE_CANCELLATION } from 'MeetingsLib/constants/errors';
import { getLongDisplayName } from 'MeetingsLib/utils/utils';
export default class Error extends Component {
  constructor(...args) {
    super(...args);

    this.handleReloadPage = () => {
      const {
        meetingRescheduleFailed,
        meetingRescheduleError
      } = this.props;
      const retryRebook = meetingRescheduleError === REBOOK_CONFLICT;

      if (meetingRescheduleFailed && !retryRebook) {
        const route = document.location.origin + document.location.pathname;
        document.location = route;
      } else {
        document.location.reload(true);
      }
    };

    this.renderDescription = (message, options = {}) => {
      return /*#__PURE__*/_jsx("p", {
        className: "error-description",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: message,
          options: options
        })
      }, "description");
    };

    this.renderEmail = () => {
      const {
        profile
      } = this.props;
      return /*#__PURE__*/_jsx("div", {
        className: "error-email",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "error.emailLink_jsx",
          options: {
            fullName: getLongDisplayName(profile),
            emailAddress: profile.email,
            linkProps: {
              href: `mailto:${profile.email}`
            }
          },
          elements: {
            Link: UILink
          }
        })
      }, "email");
    };

    this.renderRetryButton = desiredMessage => {
      const {
        meetingRescheduleError
      } = this.props;
      const retryRebook = meetingRescheduleError === REBOOK_CONFLICT;
      const message = desiredMessage || 'error.button';
      return /*#__PURE__*/_jsx("div", {
        className: "error-button-container",
        children: /*#__PURE__*/_jsx(UIButton, {
          use: retryRebook ? 'primary' : 'tertiary',
          className: retryRebook ? '' : 'error-button',
          onClick: this.handleReloadPage,
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: message
          })
        })
      }, "retry");
    };

    this.renderHeader = message => {
      return /*#__PURE__*/_jsx(H4, {
        className: "m-bottom-2",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: message
        })
      }, "header");
    };

    this.renderCreateErrorWithReload = () => {
      return /*#__PURE__*/_jsxs(UIErrorMessage, {
        title: this.renderHeader('error.general.header'),
        children: [this.renderDescription('error.general.retryDescription'), this.renderRetryButton(), this.renderEmail()]
      });
    };

    this.renderRescheduleErrorWithReload = (descriptionMessage, buttonMessage) => {
      const desiredDescription = descriptionMessage || 'error.general.retryDescription';
      return /*#__PURE__*/_jsxs(UIErrorMessage, {
        title: this.renderHeader('error.general.header'),
        children: [this.renderDescription(desiredDescription), this.renderRetryButton(buttonMessage), this.renderEmail()]
      });
    };

    this.renderRescheduleConflict = () => {
      const buttonMessage = 'error.buttonRebookConflict';
      const desiredDescription = 'error.general.rebookConflictError';
      return /*#__PURE__*/_jsxs(UIErrorMessage, {
        title: this.renderHeader('error.general.header'),
        illustration: "meetings",
        children: [this.renderDescription(desiredDescription), this.renderRetryButton(buttonMessage)]
      });
    };

    this.renderInactiveState = () => {
      const {
        NotFoundMarker
      } = this.props;
      return /*#__PURE__*/_jsxs(UIErrorMessage, {
        type: "notFound",
        title: this.renderHeader('error.downgraded.header'),
        children: [this.renderDescription('error.downgraded.description'), this.renderEmail(), /*#__PURE__*/_jsx(NotFoundMarker, {})]
      });
    };

    this.renderLinkNotFound = () => {
      const {
        NotFoundMarker
      } = this.props;
      return /*#__PURE__*/_jsxs(UIErrorMessage, {
        className: "link-not-found",
        type: "notFound",
        title: this.renderHeader('error.general.header'),
        children: [this.renderDescription('error.general.genericDescription'), /*#__PURE__*/_jsx(NotFoundMarker, {})]
      });
    };

    this.renderError = () => {
      const {
        ErrorMarker
      } = this.props;
      Raven.captureMessage('Error Marker rendered', {
        extra: {
          state: this.props
        },
        level: 'warning'
      });
      return /*#__PURE__*/_jsxs(UIErrorMessage, {
        children: [this.renderDescription('error.general.errorDescription'), /*#__PURE__*/_jsx(ErrorMarker, {})]
      });
    };

    this.renderRescheduleFailure = () => {
      const {
        meetingRescheduleError,
        profile
      } = this.props;
      const rebookErrorMessage = 'error.general.rebookGeneralError';
      const rebookButtonMessage = 'error.buttonRebook';

      switch (meetingRescheduleError) {
        case MEETING_PREVIOUSLY_CANCELLED:
          return this.renderRescheduleErrorWithReload(rebookErrorMessage, rebookButtonMessage);

        case REBOOK_CONFLICT:
          return this.renderRescheduleConflict();

        case EXCHANGE_CANCELLATION:
          Raven.captureException(meetingRescheduleError, {
            extra: {
              profile
            },
            level: 'error'
          });
          return this.renderCreateErrorWithReload();

        default:
          return this.renderCreateErrorWithReload();
      }
    };

    this.renderDifferentMessages = () => {
      const {
        meetingCreationFailed,
        bookInfoFailed,
        bookInfoError,
        meetingsUserFetchSuccess,
        meetingRescheduleFailed
      } = this.props;

      if (meetingRescheduleFailed) {
        return this.renderRescheduleFailure();
      }

      if (meetingCreationFailed) {
        return this.renderCreateErrorWithReload();
      }

      if (bookInfoFailed && (bookInfoError === NOT_FOUND || bookInfoError === USER_NOT_FOUND)) {
        if (meetingsUserFetchSuccess) {
          return this.renderInactiveState();
        }

        return this.renderLinkNotFound();
      }

      return this.renderError();
    };
  }

  componentDidMount() {
    if (this.props.meetingCreationFailed) {
      this.props.onFailure();
    }
  }

  render() {
    return /*#__PURE__*/_jsx("div", {
      className: "meetings-error text-center",
      children: this.renderDifferentMessages()
    });
  }

}
Error.propTypes = {
  meetingCreationFailed: PropTypes.bool.isRequired,
  meetingRescheduleFailed: PropTypes.bool,
  meetingRescheduleError: PropTypes.string,
  profile: PropTypes.instanceOf(UserProfile),
  bookInfoFailed: PropTypes.bool,
  bookInfoError: PropTypes.string,
  meetingsUserFetchSuccess: PropTypes.bool,
  onFailure: PropTypes.func,
  NotFoundMarker: PropTypes.func.isRequired,
  ErrorMarker: PropTypes.func.isRequired
};